const { formatMoney, getElements, getElement } = window.BAO.Utils
const preBuyUtils = new window.PreBuyUtils()

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  backInStock: '[data-product-back-in-stock]',
  formWrapper: '[data-product-form-wrapper]',
  form: '[data-product-form]',
  price: '[data-product-price]',
  priceCompare: '[data-product-price-compare]',
  priceCompareText: '[data-product-price-compare-text]',
  sku: '[data-product-sku]',
  lineItemProperties: '[data-line-items]',
  estimatedDispatch: '[data-product-estimated-dispatch]',
}

class Variant {
  constructor (data, element) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
    /**
     * @type {VariantOptions}
     * @public
     */
    this.element = element
  }

  render (event, preBuyData = false) {
    this.setAvailability(event, preBuyData)
    this.setPrice()
    this.updateInput()
  }

  setPrice () {
    unhidePriceEls()

    if (
      window.BAO.discount &&
      window.BAO.discount > 0 &&
      !this.element.hasAttribute('data-sale')
    ) {
      const discountRate = (100 - window.BAO.discount) / 100
      const discountedPrice = this.data.price * discountRate

      const priceElements = getElements(selectors.price)

      priceElements.forEach(el => {
        el.innerHTML = `${formatMoney(discountedPrice)
          .replace('.00', '')
          .replace(',', ' ')} ${window.Shopify.currency.active}`
      })

      const priceCompareElements = getElements(selectors.priceCompare)

      priceCompareElements.forEach(el => {
        el.innerHTML = `${formatMoney(this.data.price)
          .replace('.00', '')
          .replace(',', ' ')} ${window.Shopify.currency.active}`
      })
    } else {
      const priceElements = getElements(selectors.price)

      priceElements.forEach(el => {
        el.innerHTML = `${formatMoney(this.data.price)
          .replace('.00', '')
          .replace(',', ' ')} ${window.Shopify.currency.active}`
      })

      const priceCompareElements = getElements(selectors.priceCompare)

      priceCompareElements.forEach(el => {
        el.innerHTML = `${formatMoney(this.data.compare_at_price)
          .replace('.00', '')
          .replace(',', ' ')} ${window.Shopify.currency.active}`
      })

      if (!this.data.compare_at_price) {
        hidePriceCompareElements()
      }
    }
  }

  setAvailability (event, preBuyData = false) {
    const productFormWrappers = getElements(selectors.formWrapper)

    const backInStockButtons = getElements(selectors.backInStock)

    const preOrder =
      (preBuyData?.enabled && preBuyData.variantPool !== undefined) ||
      getElement(`[data-pre-order="true"][data-variant-id='${this.id}']`)

    // Check if we should show sold out instead of add to cart
    // When PreBuy is enabled but no compatible campaign exists
    const shouldShowSoldOut =
      preBuyData?.enabled && !preBuyData?.variantPool && !preOrder

    const atcLabel = preOrder
      ? window.theme.strings.preOrder
      : window.theme.strings.addToCart

    getElements(selectors.addToCart).forEach(
      el => (el.disabled = shouldShowSoldOut || !this.data.available),
    )

    getElements(selectors.addToCartText).forEach(
      el =>
        (el.textContent = shouldShowSoldOut
          ? window.theme.strings.soldOut
          : this.data.available
            ? atcLabel
            : window.theme.strings.soldOut),
    )

    if (backInStockButtons && Object.keys(backInStockButtons).length === 0) {
      return
    }

    productFormWrappers.forEach(productFormWrapper => {
      productFormWrapper.setAttribute(
        'aria-hidden',
        !backInStockButtons ? false : !this.data.available,
      )
    })

    backInStockButtons.forEach(backInStockButton => {
      backInStockButton.classList.remove('bis-BackInStock-formVisible')
      backInStockButton.setAttribute('aria-hidden', this.data.available)
      backInStockButton.setAttribute('data-variant-id', this.data.id)
    })
  }

  updateInput () {
    const inputs = getElements(`form[action*="/cart/add"] [name="id"]`)

    inputs.forEach(input => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  /**
   * @param {string} name
   * @param {string} display
   *
   * @returns {void}
   */
  updateIndicator (name, display) {
    if (!this.element.els['variant-indicator']?.exists) return

    const indicator = this.element.els['variant-indicator'].elements.find(
      el => el.dataset.name === name,
    )
    if (!indicator) return

    indicator.textContent = display
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach(shareButton => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  updateProperties (event) {
    if (document.querySelector('[data-line-items]')) {
      if (event?.dataset?.preorder === 'true') {
        document
          .querySelector('[data-line-items]')
          .insertAdjacentHTML(
            'beforeend',
            `<input type="hidden" name="properties[_preorder]" value="true">`,
          )

        if (event?.dataset?.dispatch) {
          document
            .querySelector('[data-line-items]')
            .insertAdjacentHTML(
              'beforeend',
              `<input type="hidden" name="${window.theme.strings.estimatedDispatchPrefix}" value="${event.dataset.dispatch}">`,
            )
        }
      } else {
        document.querySelector('[data-line-items]').innerHTML = ''
      }
    }

    if (document.querySelector(selectors.estimatedDispatch)) {
      if (event?.dataset?.preorder === 'true' && event?.dataset?.dispatch) {
        document
          .querySelectorAll(selectors.estimatedDispatch)
          .forEach(dispatchEl => {
            dispatchEl.innerHTML = `${window.theme.strings.estimatedDispatchPrefix}: ${event.dataset.dispatch}`
          })
      } else {
        document
          .querySelectorAll(selectors.estimatedDispatch)
          .forEach(dispatchEl => {
            dispatchEl.innerHTML = ''
          })
      }
    }
  }

  updatePreBuyProperties (data) {
    const sellingPlanId =
      data?.sellingPlanId?.replace('gid://shopify/SellingPlan/', '') || ''

    const date = new Date(data?.zonedEstDispatchDate)
    const month = date.getMonth() + 1
    const dateFormatted = `${String(month).padStart(2, '0')}/${String(
      date.getDate(),
    ).padStart(2, '0')}/${date.getFullYear()}`

    getElements(`${selectors.lineItemProperties}`).forEach(el => {
      el.innerHTML = `
        <input type="hidden" name="properties[_preorder]" value="true">
        <input type="hidden" name="properties[${
          window.theme.strings.estimatedDispatchPrefix
        }]" value="${data?.estDispatchDateFormatted}">
        <input type="hidden" name="properties[_pool_location]" value="${
          data?.location
        }">
        <input type="hidden" name="properties[_locationId]" value="${
          data?.locationId
        }">
        <input type="hidden" name="properties[_dispatch_date]" value="${
          data.zonedEstDispatchDate
        }">
        <input type="hidden" name="properties[_start_date]" value="${dateFormatted}">
        <input type="hidden" name="selling_plan" value="${
          sellingPlanId ? parseInt(sellingPlanId, 10) : null
        }">
      `
    })

    const estimatedDispatch = document.querySelectorAll(
      selectors.estimatedDispatch,
    )

    if (data?.estDispatchDateFormatted) {
      estimatedDispatch.forEach(el => {
        el.innerHTML = `${window.theme.strings.estimatedDispatchPrefix} ${data?.estDispatchDateFormatted}`
      })
    } else {
      estimatedDispatch.forEach(el => {
        el.innerHTML = ''
      })
    }
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const params = new URLSearchParams(window.location.search)
    params.set('variant', this.id.toString())

    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)
    url.search = params.toString()

    return url
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant, this)]
  }

  setupListeners () {
    super.setupListeners()

    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)
  }

  async onVariantChange (e) {
    const variant = this.getVariant()

    this.dispatchVariantChangeEvent(e, variant)

    // Legacy & PreBuy pre-order data
    const legacyPreOrder = e?.target?.dataset?.preOrder === 'true'
    const preBuyData = await this.getPreBuyData(
      e.target.dataset.productId,
      variant.id,
    )

    variant.render(e.target, preBuyData)
    variant.updateIndicator(e.target.name, e.target.dataset.display)

    if (!this.currentVariant) {
      this.resetPage(variant)

      return
    }

    variant.setUrl()
    variant.setShareUrl()

    if (
      !legacyPreOrder &&
      preBuyData &&
      preBuyData?.enabled &&
      preBuyData?.variantPool
    ) {
      variant.updatePreBuyProperties(preBuyData?.variantPool)
    } else {
      variant.updateProperties(e.target)
    }
  }

  async getPreBuyData (productId, currentVariantId) {
    const preBuyData = await preBuyUtils.getPreBuyData(
      productId,
      currentVariantId,
    )

    return preBuyData
  }

  dispatchVariantChangeEvent (event, variant) {
    this.dispatchEvent(
      new CustomEvent(window.BAO.EVENTS.VARIANT.CHANGE, {
        detail: {
          event,
          variant,
        },
        bubbles: true,
      }),
    )
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage (variant) {
    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data, this)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return this.collectedVariants[0]
    }

    const existingVariant = this.collectedVariants.find(
      variant => variant.id === this?.currentVariant?.id,
    )
    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError(`selectedOptions needs to be implemented on this class`)

    return []
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.find(variant => {
      return variant.options.every(
        (option, index) => this.selectedOptions[index].value === option,
      )
    })
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }
}

export class VariantRadios extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset
      const choice = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find(el => el.checked)?.value

      return {
        label: name,
        value: choice,
      }
    })
  }
}

export class VariantSelects extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset

      return {
        label: name,
        value: el.querySelector('select').value,
      }
    })
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios)
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects)
}

function hideElements (elements) {
  elements.forEach(el => (el.hidden = true))
}

function unhideElements (elements) {
  elements.forEach(el => (el.hidden = false))
}

function unhidePriceEls () {
  unhideElements(getElements(`${selectors.price}, ${selectors.priceCompare}`))
}

function hidePriceCompareElements () {
  hideElements(getElements(selectors.priceCompare))
}
